import { FC, ReactElement, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuthContext } from '@/hooks';
import { IUser, Role } from '@/models';
import { MasterIndexSection } from '@/pages/MasterIndex/Permissions';
import useMasterIndexAccess from '@/pages/MasterIndex/useMasterIndexAccess';
import { AuthRoutes } from '@/routes/RoutePaths';
import {
  getAllowedSidebarItems,
  isTargetRole,
  validateRoute,
} from '@/services';
import { useUserStore, useAgencyStore } from '@/store';

interface RouteGuardProps {
  children?: ReactElement | null;
}

export const GuestGuard: FC<RouteGuardProps> = ({ children }) => {
  const { accessToken } = useAuthContext();
  const { state, pathname } = useLocation();

  let nextPath: string =
    state?.path == null || state.path === '/auth/login' ? '/' : state.path;

  if (accessToken && pathname !== AuthRoutes.ForgotPassword) {
    if (state?.search) {
      nextPath += state.search;
    }
    return <Navigate to={nextPath} />;
  }

  return <>{children}</>;
};

export const DemoGuard: FC<RouteGuardProps> = ({ children }) => {
  const { account } = useUserStore();
  if (!account?.settings.demoMode) {
    return <Navigate to="/" />;
  }
  return <>{children}</>;
};

export const AuthGuard: FC<RouteGuardProps> = ({ children }) => {
  const { accessToken } = useAuthContext();
  const { account, user } = useUserStore();
  const { agencies } = useAgencyStore();
  const { pathname, search } = useLocation();
  const [requestedLocation, setRequestedLocation] = useState<string | null>();

  if (!accessToken) {
    return (
      <Navigate
        to="/auth/login"
        replace
        state={{
          path: pathname,
          search,
        }}
      />
    );
  }

  if (!account?._id || !user || !agencies.length) {
    return null;
  }
  if (!validateRoute(pathname, user, account)) {
    const initialItem = getAllowedSidebarItems(user, account)[0];
    return <Navigate to={initialItem?.link || '404'} />;
  }

  if (requestedLocation && location.pathname !== requestedLocation) {
    setRequestedLocation(null);
    return <Navigate to={requestedLocation} />;
  }

  return <>{children}</>;
};

export const CallLogGuard: FC<RouteGuardProps> = ({ children }) => {
  const { user } = useUserStore();

  if (!isTargetRole(user as IUser, Role.CALL_LOG_ACCESS)) {
    return <Navigate to="/" />;
  }
  return <>{children}</>;
};

export const InternalAffairsGuard: FC<RouteGuardProps> = ({ children }) => {
  const { user } = useUserStore();

  // Allow only if user has IA_OFFICER role
  if (isTargetRole(user as IUser, Role.IA_OFFICER)) {
    return <>{children}</>;
  }

  return <Navigate to="/tools" />;
};

export const RequireMasterIndexAccess = ({
  children,
}: {
  children: ReactElement;
}) => {
  const { canSeeMasterIndex } = useMasterIndexAccess();
  if (!canSeeMasterIndex) return <Navigate to="/my-cfs" replace />;
  return children;
};

export const RequireMasterIndexSection = ({
  section,
  children,
}: {
  section: MasterIndexSection;
  children: ReactElement;
}) => {
  const { allowed } = useMasterIndexAccess();
  if (!allowed.includes(section)) return <Navigate to="/my-cfs" replace />;
  return children;
};

/** Used for index route under /master-index to jump to the right first section */
export const MasterIndexIndexRedirect = () => {
  const { defaultSection } = useMasterIndexAccess();
  if (!defaultSection) return <Navigate to="/my-cfs" replace />;

  // Map section -> your route segment
  const to =
    defaultSection === 'persons'
      ? '/master-index/persons'
      : defaultSection === 'organizations'
      ? '/master-index/organizations'
      : defaultSection === 'vehicles'
      ? '/master-index/vehicles'
      : defaultSection === 'properties'
      ? '/master-index/properties'
      : defaultSection === 'address'
      ? '/master-index/address'
      : '/master-index/logs';

  return <Navigate to={to} replace />;
};
